/*! poster-size.js */
import Log from "app/utils/log";
import findIndex from "array-find-index";
import { parseAspectRatio } from "../../utils/aspectRatioUtils";
const log = new Log("PosterSize");
const TEMPLATE_IMAGE_REGEXP = /(.*)-1x-1\.(.*)/;

const DEFAULT_ASPECT_RATIO_16_9 = 0.5625;

/**
 * @class PosterSize
 */
class PosterSize {

    /**
     * @static
     * @param {string} image
     * @returns {boolean}
     */
    static isTemplateImage(image) {
        return TEMPLATE_IMAGE_REGEXP.test(image);
    }

    /**
     * @static
     * @param {number} width
     * @param {number} height
     * @returns {{width, height}[]}
     */
    // Given a width and height of resolution (16:9, or 9:16, or 1:1 or 4:3), dynamically generate three
    // poster images sizes ordered from smallest to largest
    static generateSizes(width, height) {
        // By default, the smallest width size on 16:9 was 320 pixels. That is used as the base of the smallest
        // dimension to begin the sizing. In other words, MAX(width, height) * factor >= 320.
        // We want the longest dimension (w or h) of the first size to be at least 320 px.
        // The other dimension will be scaled to match the desired aspect ratio (base dimension).
        // We multiply and divide by 10 to ensure that the base width and height is divisible by 10
        // We compute the next three sizes that are multiples of the base dimension.
        function roundToNearest10(number) {
            return Math.ceil(number / 10) * 10;
        }
        const SCALE_TO_FACTOR = 320;
        const factor = roundToNearest10(SCALE_TO_FACTOR / Math.max(width, height));
        const width_base = factor * width;
        const height_base = factor * height;

        return Array.from({ length: 3 }, (_, i) => {
            return {
                width: width_base * (i + 1),
                height: height_base * (i + 1)
            };
        });
    }

    /**
     * @static
     * @param {string} containerWidth
     * @param {string} containerHeight
     * @param {string} templateImage
     * @param {string} aspectRatio
     * @returns {string|null}
     */
    // Dynamically allocate poster image sizes based off of aspect ratio
    static closestImage(containerWidth, containerHeight, templateImage, aspectRatio) {

        const { width, height } = parseAspectRatio(aspectRatio);
        const posterImageSizes = this.generateSizes(width, height);

        if (!PosterSize.isTemplateImage(templateImage)) {
            log.warn("Cannot resize: Invalid template image", templateImage);
            return templateImage;
        }

        containerWidth = parseFloat(containerWidth);
        containerHeight = parseFloat(containerHeight);

        if (containerWidth <= 0) {
            log.warn("Cannot resize: Invalid container size", containerWidth, containerHeight);
            return templateImage;
        }

        // assume a default aspect ratio if height is 0
        if (containerHeight <= 0) {
            containerHeight = containerWidth * DEFAULT_ASPECT_RATIO_16_9;
        }
        let closestIndex = findIndex(posterImageSizes, size => {
            return size.width >= containerWidth && size.height >= containerHeight;
        });

        if (closestIndex === -1) {
            closestIndex = posterImageSizes.length - 1;
        }

        const closest = posterImageSizes[closestIndex];
        return templateImage.replace(TEMPLATE_IMAGE_REGEXP, `$1${ closest.width }x${ closest.height }.$2`);
    }
}

export default PosterSize;
